import ErrorDialog from '@components/ErrorDialog';
import MessageLayout from '@components/MessageLayout';
import { APP_URL, USE_BLS_THEME } from '@constants';
import { useRegisterContext } from '@contexts/RegisterProvider';
import { Button, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { resolveUrl } from '~/utils/urlUtils';

import { useValidateRegistrationCodeMutation } from '../queries/useValidateRegistrationCodeMutation';

export const CheckRegistrationLink: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registrationCode, reset } = useRegisterContext();

  const [error, setError] = useState(() => (registrationCode ? '' : 'checkRegistrationLink.errors.invalidLink'));

  const validateRegistrationCodeMutation = useValidateRegistrationCodeMutation({
    onSuccess: () => {
      void navigate('/auth', { replace: true });
    },
    onError: ({ commonErrorCode, isExpired }) => {
      if (commonErrorCode) {
        setError(commonErrorCode);
      } else {
        setError(`checkRegistrationLink.errors.${isExpired ? 'expiredLink' : 'invalidLink'}`);
      }
    },
  });

  const hasValidatedRef = useRef(false);
  useEffect(() => {
    if (hasValidatedRef.current) return;
    hasValidatedRef.current = true;

    if (!registrationCode) return;
    validateRegistrationCodeMutation.mutate(registrationCode);
  }, [registrationCode, validateRegistrationCodeMutation]);

  const handleRestart = () => {
    reset();
    void navigate('/', { replace: true });
  };

  return (
    <>
      <MessageLayout
        icon={!error && <CircularProgress size={32} />}
        title={t('checkRegistrationLink.title')}
        text={t('checkRegistrationLink.message')}
      />
      <ErrorDialog
        open={!!error}
        errorKey={error}
        title={t('checkRegistrationLink.errors.title')}
        body={t(error)}
        alertBox={{ textKey: 'checkRegistrationLink.errors.alertText', severity: 'warning' }}
        primaryAction={
          <>
            <Button variant="contained" href={resolveUrl(APP_URL)} role="link">
              {t('checkRegistrationLink.actions.login', { app: USE_BLS_THEME ? 'Bikeleasing' : 'Probonio' })}
            </Button>
            <Button variant="text" onClick={handleRestart} sx={{ fontWeight: 'bold' }}>
              {t('checkRegistrationLink.actions.useCodeInstead')}
            </Button>
          </>
        }
        footerLink={false}
      />
    </>
  );
};
